import { useCallback, useEffect, useRef, useState } from "react";
import { isKnownScreen } from "@racedash/shared";
import type { ScreenEntry } from "@racedash/shared";
import { useSwipe } from "./useSwipe";
import "./switcher.css";

/**
 * Switching screens while full screen.
 *
 * The tab strip lives above the stage, and going full screen takes only the
 * stage — so there was no way to leave the screen you started on, which is
 * exactly when you most want to: full screen is the pit-wall mode.
 *
 * Rendered inside the stage and shown only when that stage is actually the
 * full-screen element, so it never doubles the tabs that are already there.
 *
 * It behaves like a video player's controls: visible when the pointer moves,
 * gone a few seconds later. A permanent strip would eat the top of a dashboard
 * whose whole point is filling the screen, and a dashboard is looked at far
 * more than it is operated.
 */
export default function ScreenSwitcher({
  screens,
  activeId,
  onSelect,
  stageRef,
}: {
  screens: ScreenEntry[];
  activeId: string | null;
  onSelect: (id: string) => void;
  stageRef: React.RefObject<HTMLElement | null>;
}) {
  const fullscreen = useFullscreen(stageRef);
  const [visible, setVisible] = useState(true);
  const hideAt = useRef<number | null>(null);

  const index = screens.findIndex((s) => s.id === activeId);
  const at = index >= 0 ? index : 0;

  // Swipe, which is the natural gesture on the tablet a pit wall often is.
  // Wrapping rather than stopping at the ends: with three screens, refusing to
  // go left from the first is a gesture that does nothing for no reason a
  // finger can see.
  const step = useCallback(
    (by: -1 | 1) => {
      const next = screens[(at + by + screens.length) % screens.length];
      if (next) onSelect(next.id);
    },
    [screens, at, onSelect],
  );
  useSwipe(stageRef, fullscreen && screens.length > 1, step);

  // Keyboard, because a pit wall may have a keyboard and no mouse to hand, and
  // because reaching for a control that is currently hidden is a poor way to
  // reveal it. Digits rather than arrows: arrows nudge a widget in the editor,
  // and one binding meaning two things is how a shortcut becomes a hazard.
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      const n = Number(e.key);
      if (!Number.isInteger(n) || n < 1 || n > screens.length) return;
      const target = screens[n - 1];
      if (target) {
        onSelect(target.id);
        setVisible(true);
        hideAt.current = Date.now() + revealMs;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen, screens, onSelect]);

  const reveal = useCallback(() => {
    setVisible(true);
    hideAt.current = Date.now() + revealMs;
  }, []);

  useEffect(() => {
    if (!fullscreen) return;
    const el = stageRef.current;
    if (!el) return;

    reveal();
    el.addEventListener("pointermove", reveal);
    // One timer rather than a fresh timeout per pointer move, which on a real
    // mouse is hundreds a second.
    const tick = setInterval(() => {
      if (hideAt.current !== null && Date.now() > hideAt.current)
        setVisible(false);
    }, 500);

    return () => {
      el.removeEventListener("pointermove", reveal);
      clearInterval(tick);
    };
  }, [fullscreen, stageRef, reveal]);

  if (!fullscreen || screens.length < 2) return null;

  return (
    <>
      {/*
        Always up, faintly. The switcher itself hides so it does not eat the
        screen, but a dashboard that hides every trace of its other screens
        leaves no sign they exist — and nobody swipes looking for something they
        do not know is there. Dots are the one idiom everybody already reads as
        "there are more of these, and you are on the second".
      */}
      <div className="screen-dots" aria-hidden data-dim={!visible || undefined}>
        {screens.map((s, i) => (
          <span
            key={s.id}
            className="screen-dot"
            data-active={i === at || undefined}
          />
        ))}
      </div>

      <nav
        className="screen-switcher"
        data-visible={visible || undefined}
        aria-label="Screens"
      >
        {screens.map((s, i) => (
          <button
            key={s.id}
            type="button"
            className="screen-switcher-tab"
            data-active={s.id === activeId || undefined}
            onClick={() => onSelect(s.id)}
          >
            <span className="screen-switcher-key">{i + 1}</span>
            {s.name}
            {!isKnownScreen(s) && " ⚠"}
          </button>
        ))}
      </nav>
    </>
  );
}

/** How long the controls stay up after the pointer stops. */
const revealMs = 2500;

/** Whether this element is the one currently filling the screen. */
function useFullscreen(ref: React.RefObject<HTMLElement | null>): boolean {
  const [full, setFull] = useState(false);

  useEffect(() => {
    const check = () => setFull(document.fullscreenElement === ref.current);
    check();
    document.addEventListener("fullscreenchange", check);
    return () => document.removeEventListener("fullscreenchange", check);
  }, [ref]);

  return full;
}
